import { DateTime } from 'luxon'

/**
 * Parses a date string in the profile-specific format and returns a Luxon DateTime object.
 * Strictly validates the date to ensure it matches the input.
 * Attempts parsing in the following order:
 * 1. ISO format.
 * 2. Custom timestamptz format (e.g., 'yyyy-MM-dd HH:mm:ss ZZZ UTC').
 * 3. Fallback to native Date parsing.
 *
 * @param {string} dateStr - The date string to parse.
 * @returns {DateTime} Luxon DateTime object.
 */
DateTime.fromProfileDate = function(dateStr) {
  // Check that we have a value to validate
  try {
    // Check if we are already an instance of DateTime
    if (DateTime.isDateTime(dateStr)) {
      return dateStr
    }

    if (!dateStr || typeof dateStr !== 'string') {
      return DateTime.invalid('Invalid date')
    }

    const validate = (dt, str) => {
      if (!dt.isValid) {
        return false
      }

      str = str.replaceAll('-', '')
      const strYear = str.slice(0, 4)
      const strMonth = str.slice(4, 6)
      const strDay = str.slice(6, 8)

      return `${strYear}-${strMonth}-${strDay}` === dt.toFormat('yyyy-MM-dd')
    }

    // Attempt to parse using ISO format
    let dt = DateTime.fromISO(dateStr, { zone: 'utc' })
    if (validate(dt, dateStr)) {
      return dt
    }

    // Attempt to parse using custom timestamptz format
    dt = DateTime.fromFormat(dateStr, "yyyy-MM-dd HH:mm:ss ZZZ 'UTC'", { zone: 'utc' })
    if (validate(dt, dateStr)) {
      return dt
    }

    // Fallback to native Date parsing
    dt = DateTime.fromJSDate(new Date(dateStr))
    if (validate(dt, dateStr)) {
      return dt
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e, dateStr)
  }

  return DateTime.invalid('Invalid date')
}

export { DateTime }
